import React, { useState, useEffect, useContext } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import ModalWrapper from '../../../components/Modals/ModalWrapper';
import FontSizes from '../../../constants/FontSizes';
import { MainColors } from '../../../constants/Colors';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import FormTextInput from '../../../components/FormTextInput';
import MainButton from '../../../components/Buttons/MainButton';
import { AuthContext } from '../../../context/AuthProvider';
import CustomSnackbar from '../../../components/CustomSnackbar';
import useFetch from '../../../utils/useFetch'

const EmailAddressModal = (props) => {
    const { jwt } = useContext(AuthContext);
    const { email } = useContext(AuthContext);
    const [enteredEmail, setEnteredEmail] = useState(email);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [showVerifyCodeStep, setShowVerifyCodeStep] = useState(false);
    const [verificationCode, setVerificationCode] = useState('')
    const { put, isLoading } = useFetch()

    useEffect(() => {
        if(isLoading){
            setSaveButtonDisabled(true);
        }
        else if (enteredEmail && enteredEmail !== email) {
            setSaveButtonDisabled(false);
        } else {
            setSaveButtonDisabled(true);
        }
    }, [enteredEmail, isLoading]);

    const onSaveEmailChangesHandler = async () => {
        try{
            const serverEmail = await put('/api/settings/email/send_code', {
                newEmail: enteredEmail,
            })
    
            setSnackbarMessage('Verification Token Sent.');
            setSnackbarVisible(true);
            setShowVerifyCodeStep(true)
            setVerificationCode('')
        }catch (e){
            throw e
        }
    }

    const onVerificationCodeSubmittedHandler = async () => {
        try{
            const response = await put('/api/settings/email/verify_code', {
                code: verificationCode,
            })
    
            setSnackbarMessage('Email changes were saved.');
            setSnackbarVisible(true);
            setShowVerifyCodeStep(false)
            setVerificationCode('')
            props?.onEmailUpdateCompleted()
        }catch (e){
            throw e
        }
    }

    const onSnackbarDismissedHandler = () => {
        setSnackbarVisible(false);
    }

    return (
        <>
            <ModalWrapper visible={props.visible} onModalBackgroundPressed={props.onModalBackgroundPressed}>
                <View style={styles.container}>
                    <View style={styles.headerContainer}>
                        <Text style={styles.title}>My Email Address</Text>
                        <TouchableOpacity onPress={props.onCloseButtonPressed} accessibilityLabel='Close'>
                            <MaterialCommunityIcons name="close" size={16} color={MainColors.mediumGray} />
                        </TouchableOpacity>
                    </View>
                    <View style={styles.contentContainer}>
                        { !showVerifyCodeStep ?
                            <>
                                <FormTextInput
                                    label="Email Address"
                                    keyboardType="email-address"
                                    accessibilityLabel="Email address"
                                    accessibilityHint="View and edit the email address here"
                                    defaultValue={email}
                                    value={enteredEmail}
                                    onChangeText={(enteredEmail) => setEnteredEmail(enteredEmail)}
                                    editable={true}
                                />
                                <MainButton
                                    style={{ backgroundColor: saveButtonDisabled ? MainColors.lightGray : MainColors.primary }}
                                    text="Save Changes"
                                    accessibilityLabel='Save Changes to Email Address'
                                    accessibilityHint='Press this button to save changes to your email address'
                                    disabled={saveButtonDisabled}
                                    onPress={() => onSaveEmailChangesHandler()}
                                />
                            </>
                        :   
                            <>
                                <FormTextInput
                                    label="Verification Code"
                                    keyboardType="numeric"
                                    accessibilityLabel="Verification Code"
                                    accessibilityHint="Enter your verification code to verify email"
                                    value={verificationCode}
                                    onChangeText={(v) => setVerificationCode(v)}
                                    editable={true}
                                />
                                <MainButton
                                    style={{ backgroundColor: saveButtonDisabled ? MainColors.lightGray : MainColors.primary }}
                                    text="Confirm Email Verification"
                                    accessibilityLabel='Save Changes to Email Address'
                                    accessibilityHint='Press this button to save changes to your email address'
                                    disabled={saveButtonDisabled}
                                    onPress={() => onVerificationCodeSubmittedHandler()}
                                />
                            </> 
                        }
                    </View>
                </View>
            </ModalWrapper>
            <CustomSnackbar
                message={snackbarMessage}
                visible={snackbarVisible}
                onDismiss={onSnackbarDismissedHandler}
                actionLabel='DONE'
                actionOnPress={() => setSnackbarVisible(false)}
            />
        </>
    )
}

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 20,
        width: '100%'
    },
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: MainColors.faintGray,
        paddingBottom: 10
    },
    title: {
        fontSize: FontSizes.medium,
        fontWeight: '600',
        color: MainColors.darkGray,
        textAlign: 'center',
    },
    text: {
        color: MainColors.darkGray,
        fontSize: FontSizes.small,
        fontWeight: '600',
    },
    contentContainer: {
        marginTop: 15
    }
});

export default EmailAddressModal;